<template>
    <Head title="Assets" />

    <div class="py-6">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="flex justify-between items-center mb-6">
                    <div class="flex items-center gap-3">
                        <div>
                            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Assets</h1>
                            <p class="mt-1 text-sm text-gray-600 dark:text-secondary-400">Manage and track your organization's assets</p>
                        </div>
                        <InfoButton title="Assets Management" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Assets page is your central hub for managing all physical assets in your maintenance
                                system. View and organize equipment, machinery, vehicles, and tools that require maintenance
                                tracking. Assets are organized by categories, locations, and status, with quick statistics
                                showing operational health and maintenance due dates at a glance.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>View Modes:</strong> Switch between Grid view (visual cards) and List view (table
                                  format) using the toggle buttons<br>
                                • <strong>Search:</strong> Type asset tag or name in the search box to quickly find specific
                                  assets<br>
                                • <strong>Filter:</strong> Use Status, Category, and Location dropdowns to narrow down the
                                  asset list<br>
                                • <strong>Quick Stats:</strong> Dashboard cards show Total Assets, Operational count, Down
                                  assets, and Maintenance Due items<br>
                                • <strong>Create Asset:</strong> Click "New Asset" button to register new equipment<br>
                                • <strong>Manage Categories:</strong> Click "Manage Asset Categories" to organize asset
                                  classification system<br>
                                • <strong>Asset Details:</strong> Click any asset card or row to view complete details,
                                  PM schedules, work orders, and maintenance history
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • Use the <strong>Status filter</strong> to focus on Down or Maintenance assets needing
                                  immediate attention<br>
                                • <strong>Maintenance Due counter</strong> shows assets with PM work overdue - address
                                  these proactively<br>
                                • <strong>Grid view</strong> is best for visual recognition; List view is better for
                                  bulk data review<br>
                                • <strong>Category organization</strong> helps with filtering and reporting - keep
                                  categories well-structured<br>
                                • <strong>Asset images</strong> speed up identification, especially for field technicians<br>
                                • <strong>Location tracking</strong> essential for large facilities - use hierarchical
                                  locations (Building → Floor → Room)<br>
                                • Regularly review <strong>Maintenance Due</strong> to prevent breakdowns and maintain
                                  asset reliability
                            </p>
                        </InfoButton>
                    </div>
                    <div class="flex items-center space-x-3">
                        <Link
                            :href="route('settings.maintenance.asset-categories.index')"
                            class="inline-flex items-center px-4 py-2 bg-gray-600 dark:bg-gray-700 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            Manage Asset Categories
                        </Link>
                        <Link
                            v-if="$page.props.auth.permissions.includes('maintenance.assets.create')"
                            :href="route('settings.maintenance.assets.create')"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            New Asset
                        </Link>
                    </div>
                </div>

                <!-- Statistics Cards -->
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-blue-100 rounded-md p-3">
                                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Total Assets</p>
                                <p class="text-2xl font-semibold text-gray-900 dark:text-white">{{ assets.total }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-green-100 rounded-md p-3">
                                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Operational</p>
                                <p class="text-2xl font-semibold text-green-600">
                                    {{ assets.data.filter(a => a.status === 'operational').length }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-red-100 rounded-md p-3">
                                <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Down</p>
                                <p class="text-2xl font-semibold text-red-600">
                                    {{ assets.data.filter(a => a.status === 'down').length }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="bg-white dark:bg-secondary-800 rounded-lg shadow p-6">
                        <div class="flex items-center">
                            <div class="flex-shrink-0 bg-yellow-100 rounded-md p-3">
                                <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div class="ml-4">
                                <p class="text-sm font-medium text-gray-600 dark:text-secondary-400">Maintenance Due</p>
                                <p class="text-2xl font-semibold text-yellow-600">
                                    {{ assets.data.filter(a => a.next_maintenance_date && new Date(a.next_maintenance_date) <= new Date()).length }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Filters and Search -->
                <div class="bg-white dark:bg-secondary-800 rounded-lg shadow mb-6">
                    <div class="p-4 border-b border-gray-200 dark:border-secondary-700">
                        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <!-- Search -->
                            <div class="flex-1 max-w-lg">
                                <div class="relative">
                                    <input
                                        v-model="searchQuery"
                                        type="text"
                                        placeholder="Search by asset tag or name..."
                                        class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                        @input="debounceSearch"
                                    />
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <!-- View Toggle -->
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="viewMode = 'grid'"
                                    :class="[
                                        'p-2 rounded-md',
                                        viewMode === 'grid'
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                            : 'text-gray-400 hover:text-gray-600'
                                    ]"
                                >
                                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M10 3H4a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1V4a1 1 0 00-1-1zM20 3h-6a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1V4a1 1 0 00-1-1zM10 13H4a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1v-6a1 1 0 00-1-1zM20 13h-6a1 1 0 00-1 1v6a1 1 0 001 1h6a1 1 0 001-1v-6a1 1 0 00-1-1z" />
                                    </svg>
                                </button>
                                <button
                                    @click="viewMode = 'list'"
                                    :class="[
                                        'p-2 rounded-md',
                                        viewMode === 'list'
                                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                            : 'text-gray-400 hover:text-gray-600'
                                    ]"
                                >
                                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Filter Pills -->
                    <div class="p-4 flex flex-wrap gap-2">
                        <select
                            v-model="selectedFilters.status"
                            class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Status</option>
                            <option value="operational">Operational</option>
                            <option value="down">Down</option>
                            <option value="maintenance">Under Maintenance</option>
                            <option value="retired">Retired</option>
                        </select>

                        <select
                            v-model="selectedFilters.category_id"
                            class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Categories</option>
                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>

                        <select
                            v-model="selectedFilters.location_id"
                            class="rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 focus:border-blue-500"
                            @change="applyFilters"
                        >
                            <option value="">All Locations</option>
                            <option v-for="location in locations" :key="location.id" :value="location.id">
                                {{ location.name }}
                            </option>
                        </select>

                        <button
                            v-if="hasActiveFilters"
                            @click="clearFilters"
                            class="px-3 py-1.5 text-sm text-gray-600 dark:text-secondary-300 hover:text-gray-800 dark:hover:text-white border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 rounded-md hover:bg-gray-50 dark:hover:bg-secondary-600"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                <!-- Assets Grid/List -->
                <div v-if="viewMode === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    <Link
                        v-for="asset in assets.data"
                        :key="asset.id"
                        :href="route('settings.maintenance.assets.show', asset.id)"
                        class="bg-white dark:bg-secondary-800 rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
                    >
                        <div class="aspect-w-16 aspect-h-9 bg-gray-200 dark:bg-secondary-700">
                            <img
                                v-if="asset.image"
                                :src="asset.image_url"
                                :alt="asset.name"
                                class="w-full h-48 object-cover"
                            />
                            <div v-else class="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-secondary-700">
                                <svg class="w-16 h-16 text-gray-400 dark:text-secondary-500" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                            </div>
                        </div>
                        <div class="p-4">
                            <div class="flex items-start justify-between mb-2">
                                <div class="flex-1">
                                    <p class="text-xs text-gray-500 dark:text-secondary-400">{{ asset.asset_tag }}</p>
                                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white truncate">{{ asset.name }}</h3>
                                </div>
                                <span
                                    :class="[
                                        'px-2 py-1 text-xs font-medium rounded-full',
                                        getStatusClass(asset.status)
                                    ]"
                                >
                                    {{ asset.status_label }}
                                </span>
                            </div>

                            <div class="space-y-2 text-sm text-gray-600 dark:text-secondary-400">
                                <div v-if="asset.category" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                    </svg>
                                    <span class="truncate">{{ asset.category.name }}</span>
                                </div>

                                <div v-if="asset.location" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span class="truncate">{{ asset.location.name }}</span>
                                </div>

                                <div v-if="asset.next_maintenance_date" class="flex items-center">
                                    <svg class="w-4 h-4 mr-2 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span :class="isMaintenanceDue(asset.next_maintenance_date) ? 'text-red-600 dark:text-red-400 font-medium' : ''">
                                        Next PM: {{ formatDate(asset.next_maintenance_date) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </Link>
                </div>

                <!-- Assets Table -->
                <div v-else class="bg-white dark:bg-secondary-800 rounded-lg shadow overflow-hidden mb-6">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                        <thead class="bg-gray-50 dark:bg-secondary-750">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                    Asset
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                    Category
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                    Location
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                    Next PM
                                </th>
                                <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                            <tr v-for="asset in assets.data" :key="asset.id" class="hover:bg-gray-50 dark:hover:bg-secondary-750">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-10 w-10">
                                            <img
                                                v-if="asset.image"
                                                :src="asset.image_url"
                                                :alt="asset.name"
                                                class="h-10 w-10 rounded-full object-cover"
                                            />
                                            <div v-else class="h-10 w-10 rounded-full bg-gray-200 dark:bg-secondary-700 flex items-center justify-center">
                                                <svg class="w-6 h-6 text-gray-400 dark:text-secondary-500" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-gray-900 dark:text-white">{{ asset.name }}</div>
                                            <div class="text-sm text-gray-500 dark:text-secondary-400">{{ asset.asset_tag }}</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {{ asset.category?.name || '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {{ asset.location?.name || '-' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 py-1 text-xs font-medium rounded-full',
                                            getStatusClass(asset.status)
                                        ]"
                                    >
                                        {{ asset.status_label }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm">
                                    <span
                                        v-if="asset.next_maintenance_date"
                                        :class="isMaintenanceDue(asset.next_maintenance_date) ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-900 dark:text-white'"
                                    >
                                        {{ formatDate(asset.next_maintenance_date) }}
                                    </span>
                                    <span v-else class="text-gray-400 dark:text-secondary-500">-</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <Link
                                        :href="route('settings.maintenance.assets.show', asset.id)"
                                        class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                    >
                                        View
                                    </Link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="assets.data.length > 0" class="bg-white dark:bg-secondary-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-secondary-700 sm:px-6 rounded-lg shadow">
                    <div class="flex-1 flex justify-between sm:hidden">
                        <Link
                            v-if="assets.prev_page_url"
                            :href="assets.prev_page_url"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                        >
                            Previous
                        </Link>
                        <Link
                            v-if="assets.next_page_url"
                            :href="assets.next_page_url"
                            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                        >
                            Next
                        </Link>
                    </div>
                    <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p class="text-sm text-gray-700 dark:text-secondary-300">
                                Showing
                                <span class="font-medium">{{ assets.from }}</span>
                                to
                                <span class="font-medium">{{ assets.to }}</span>
                                of
                                <span class="font-medium">{{ assets.total }}</span>
                                results
                            </p>
                        </div>
                        <div>
                            <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                <template v-for="link in assets.links" :key="link.label">
                                    <Link
                                        v-if="link.url"
                                        :href="link.url"
                                        :class="[
                                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                            link.active
                                                ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400'
                                                : 'bg-white dark:bg-secondary-700 border-gray-300 dark:border-secondary-600 text-gray-500 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-600'
                                        ]"
                                        v-html="link.label"
                                    />
                                    <span
                                        v-else
                                        :class="[
                                            'relative inline-flex items-center px-4 py-2 border text-sm font-medium cursor-not-allowed opacity-50',
                                            'bg-white dark:bg-secondary-700 border-gray-300 dark:border-secondary-600 text-gray-400 dark:text-secondary-500'
                                        ]"
                                        v-html="link.label"
                                    />
                                </template>
                            </nav>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="assets.data.length === 0" class="bg-white dark:bg-secondary-800 rounded-lg shadow p-12 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No assets found</h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Get started by creating a new asset.</p>
                    <div class="mt-6">
                        <Link
                            v-if="$page.props.auth.permissions.includes('maintenance.assets.create')"
                            :href="route('settings.maintenance.assets.create')"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            New Asset
                        </Link>
                    </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    assets: Object,
    categories: Array,
    locations: Array,
    filters: Object,
});

const viewMode = ref('grid');
const searchQuery = ref(props.filters.search || '');
const selectedFilters = ref({
    status: props.filters.status || '',
    category_id: props.filters.category_id || '',
    location_id: props.filters.location_id || '',
});

let searchTimeout = null;

const hasActiveFilters = computed(() => {
    return selectedFilters.value.status ||
           selectedFilters.value.category_id ||
           selectedFilters.value.location_id ||
           searchQuery.value;
});

const debounceSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        applyFilters();
    }, 500);
};

const applyFilters = () => {
    router.get(route('settings.maintenance.assets.index'), {
        search: searchQuery.value,
        ...selectedFilters.value,
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedFilters.value = {
        status: '',
        category_id: '',
        location_id: '',
    };
    applyFilters();
};

const getStatusClass = (status) => {
    const classes = {
        operational: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
        down: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
        maintenance: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
        retired: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
        disposed: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300';
};

const isMaintenanceDue = (date) => {
    return new Date(date) <= new Date();
};

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};
</script>
